import { ReactElement, useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Stack, Typography, TableCell, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CriteriaResult } from 'types/domainTypes';
import { Close, OpenInNew } from '@mui/icons-material';
import { useGetResultGraphQuery } from 'redux/services/calculations';
import { TableRow } from 'components/atoms/TableRow';

export const StatusChip = styled(Chip, { shouldForwardProp: (prop) => prop !== 'isPass' })<{ isPass: boolean }>(({ theme, isPass }) => ({
  backgroundColor: isPass ? theme.palette.success.light : theme.palette.error.light,
  minWidth: '60px',
  padding: 0,
  height: '28px',
  '& span': { fontSize: '13px' },
}));

interface Props {
  calculationId: string;
  criteriaResult: CriteriaResult;
  label: string;
  'data-testid'?: string;
}

export function CriteriaResultsRow({ calculationId, criteriaResult, label, 'data-testid': testId }: Props): ReactElement {
  const { t, i18n } = useTranslation();
  const { result, isPass } = criteriaResult;
  const [isGraphVisible, setIsGraphVisible] = useState(false);
  const [graphObject, setGraphObject] = useState('');
  const resultGraph = useGetResultGraphQuery(
    { calculationId: calculationId, type: criteriaResult.calculationType },
    { skip: !isGraphVisible }
  );

  const formatDecimal = (number: number) => {
    return number.toLocaleString(i18n.language, { maximumFractionDigits: 1, minimumFractionDigits: 0 });
  };

  useEffect(() => {
    if (!graphObject && resultGraph.data) {
      setGraphObject(URL.createObjectURL(new Blob([resultGraph.data], { type: 'image/svg+xml' })));
    }
  }, [graphObject, setGraphObject, resultGraph.data]);

  return (
    <>
      <TableRow data-testid={testId}>
        <TableCell component="th" scope="row">
          {label}
        </TableCell>
        <TableCell align="left">{`${formatDecimal(result * 100)}%`}</TableCell>
        <TableCell align="center">
          <StatusChip
            isPass={isPass}
            label={<Typography variant="caption">{isPass ? t('calculation.status-pass') : t('calculation.status-fail')}</Typography>}
          />
        </TableCell>
        <TableCell align="center">
          <IconButton color="primary" onClick={() => setIsGraphVisible(true)}>
            <OpenInNew sx={{ height: '22px' }} />
          </IconButton>
        </TableCell>
      </TableRow>
      <Dialog
        fullWidth
        maxWidth={'sm'}
        onClose={() => setIsGraphVisible(false)}
        open={isGraphVisible}
        scroll="paper"
        data-testid="resultGraphDialog"
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box component="span">{label}</Box>
            <IconButton onClick={() => setIsGraphVisible(false)}>
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ margin: 0, padding: 0 }}>
          <img alt="Result graph" src={graphObject} width="100%" />
        </DialogContent>
      </Dialog>
    </>
  );
}
